import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Dialog, DialogContent, DialogActions,
  Button, IconButton, LinearProgress, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getUsage, resetUsage, KIND_LABELS, PRICE_PER_1000,
  DAILY_PHOTO_REFRESH_LIMIT,
} from '../../utils/apiUsage';

export function ApiUsageChip({ onClick }) {
  const [usage, setUsage] = useState(() => getUsage());
  // Refresh the chip occasionally so numbers tick as the user interacts
  useEffect(() => {
    const id = setInterval(() => setUsage(getUsage()), 10000);
    return () => clearInterval(id);
  }, []);

  const todayPhotos = usage.today.photoRefresh || 0;
  const nearLimit = todayPhotos >= DAILY_PHOTO_REFRESH_LIMIT * 0.8;
  const cost = `$${usage.estimateUsd.toFixed(2)}`;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.6,
        cursor: 'pointer', userSelect: 'none',
        px: 1, py: 0.4, borderRadius: 99,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
        fontSize: '0.68rem', color: 'text.disabled',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.055)', color: 'text.secondary' },
      }}
    >
      <Box sx={{
        width: 6, height: 6, borderRadius: '50%',
        bgcolor: nearLimit ? '#ef5350' : '#66bb6a',
      }} />
      <Box component="span" sx={{ fontWeight: 700 }}>Maps API</Box>
      <Box component="span" sx={{ opacity: 0.75 }}>·</Box>
      <Box component="span">{todayPhotos}/{DAILY_PHOTO_REFRESH_LIMIT} today</Box>
      <Box component="span" sx={{ opacity: 0.75 }}>·</Box>
      <Box component="span">~{cost}/mo</Box>
    </Box>
  );
}

export default function ApiUsagePanel({ open, onClose }) {
  const [usage, setUsage] = useState(() => getUsage());
  useEffect(() => {
    if (!open) return;
    setUsage(getUsage());
  }, [open]);

  const kinds = Object.keys(KIND_LABELS);
  const photoCount = usage.today.photoRefresh || 0;
  const photoPct = Math.min(100, (photoCount / DAILY_PHOTO_REFRESH_LIMIT) * 100);

  const confirmReset = () => {
    if (!window.confirm('Reset the local usage counter? This does not reset anything on Google.')) return;
    resetUsage();
    setUsage(getUsage());
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { bgcolor: '#13131f', borderRadius: 3 } }}>
      <Box sx={{ px: 2.25, pt: 2, pb: 0.5, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 600, fontSize: '1.05rem', flex: 1 }}>
          Google Maps usage
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 1.5, px: 2.25 }}>
        {/* Daily photo refresh gauge */}
        <Box sx={{
          p: 1.25, mb: 1.5, borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600, flex: 1 }}>
              PHOTO REFRESHES TODAY
            </Typography>
            <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 600, fontSize: '1.1rem' }}>
              {photoCount}<Box component="span" sx={{ color: 'text.disabled', fontSize: '0.78rem' }}> / {DAILY_PHOTO_REFRESH_LIMIT}</Box>
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={photoPct}
            sx={{
              height: 5, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.05)',
              '& .MuiLinearProgress-bar': {
                bgcolor: photoPct >= 80 ? '#ef5350' : '#66bb6a',
                borderRadius: 99,
              },
            }}
          />
          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.75 }}>
            Hard-stops background photo refresh once this fills, until midnight.
          </Typography>
        </Box>

        {/* Breakdown by kind over the recorded window */}
        <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 700, letterSpacing: '0.08em', mb: 0.75 }}>
          LAST {usage.days} DAY{usage.days === 1 ? '' : 'S'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          {kinds.map(k => {
            const count = usage.monthTotals[k] || 0;
            const cost = (count / 1000) * (PRICE_PER_1000[k] || 0);
            return (
              <Box key={k} sx={{
                display: 'flex', alignItems: 'baseline', gap: 1,
                py: 0.4, borderBottom: '1px dashed rgba(255,255,255,0.04)',
              }}>
                <Typography sx={{ fontSize: '0.78rem', flex: 1 }}>{KIND_LABELS[k]}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  {count}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', width: 56, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  ${cost.toFixed(2)}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1, pt: 0.5 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, flex: 1 }}>Estimated</Typography>
          <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 600, fontSize: '1.1rem', color: 'primary.light' }}>
            ${usage.estimateUsd.toFixed(2)}
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />

        <Box sx={{
          p: 1.25, borderRadius: 2,
          bgcolor: 'rgba(79,139,249,0.06)',
          border: '1px solid rgba(79,139,249,0.14)',
        }}>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mb: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#8ab4f8', mt: 0.1 }} />
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.5 }}>
              This is a local estimate from counters in your browser. Google's real billing is in Cloud Console. Set a hard cap there — the client can only self-govern.
            </Typography>
          </Box>
          <Box
            component="a"
            href="https://console.cloud.google.com/billing/budgets"
            target="_blank"
            rel="noopener"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4,
              fontSize: '0.72rem', fontWeight: 700, color: '#8ab4f8',
              textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
            }}
          >
            Set a Cloud billing budget <OpenInNewIcon sx={{ fontSize: 11 }} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.25, pb: 2, pt: 0 }}>
        <Button size="small" onClick={confirmReset} sx={{ color: 'text.disabled', mr: 'auto' }}>
          Reset local counter
        </Button>
        <Button size="small" variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
